import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import Lightning from './ui/Lightning';

export default function WhyAIMusic() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="why-ai" className="relative py-24 md:py-32 bg-cyber-dark overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 cyber-grid opacity-10" />
      
      {/* Lightning Effects - Pink/Purple theme, less frequent */}
      <Lightning 
        interval={1400}
        probability={0.65}
        colors={['#ec4899', '#a855f7', '#8b5cf6']}
        multiBolts={false}
      />
      
      {/* Accent glows */}
      <div 
        className="absolute top-1/4 -left-32 w-64 h-64 rounded-full opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(circle, #ec4899 0%, transparent 70%)' }}
      />
      <div 
        className="absolute bottom-1/4 -right-32 w-64 h-64 rounded-full opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(circle, #00d4ff 0%, transparent 70%)' }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-4" ref={ref}>
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-orbitron text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-white">WHY </span>
            <span className="bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-cyan bg-clip-text text-transparent">
              AI MUSIC?
            </span>
          </h2>
          <div className="w-24 h-1 mx-auto bg-gradient-to-r from-cyber-pink to-cyber-cyan rounded-full" />
        </motion.div>

        {/* Main Content Card */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Glassmorphism card */}
          <div className="relative p-8 md:p-12 rounded-3xl bg-cyber-darker/60 backdrop-blur-xl border border-white/10 shadow-2xl">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-cyber-pink/50 rounded-tl-3xl" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-cyber-cyan/50 rounded-br-3xl" />
            
            {/* Opening statement */}
            <motion.p
              className="text-2xl md:text-3xl font-rajdhani text-white/90 leading-relaxed mb-8"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.4 }}
            >
              I've always loved music. <span className="text-cyber-cyan">Always.</span>
            </motion.p>

            {/* Main message */}
            <motion.div
              className="space-y-6 text-lg md:text-xl font-rajdhani text-white/70 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.6 }}
            >
              <p>
                For as long as I can remember, I've dreamed of making my own music—hearing melodies in my head, 
                feeling beats that needed to exist in the world. But here's the thing: <span className="text-white">I don't have the voice for it.</span> I 
                never had the traditional musical talent that the industry demands.
              </p>

              <p>
                Then AI changed everything.
              </p>

              <p>
                AI has given people like me—people with an <span className="text-cyber-purple font-semibold">ear for music</span> but without the conventional 
                means to create it—the ability to finally make something real. Something that sounds like what's been playing 
                in our heads for years.
              </p>

              <p className="text-white/80">
                I'm not here to get rich. I'm not trying to compete with "real" artists or replace anyone. I'm here because 
                I finally <span className="italic">can</span> be. I'm making music I enjoy, music I'm proud of, music that would 
                have stayed trapped in my imagination forever otherwise.
              </p>
            </motion.div>

            {/* Closing statement */}
            <motion.div
              className="mt-10 pt-8 border-t border-white/10"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.8 }}
            >
              <p className="text-xl md:text-2xl font-rajdhani text-white/90 mb-4">
                If you don't like it, or you disagree with AI music—that's okay. I'm not here to argue or convince anyone.
              </p>
              <p className="text-lg md:text-xl font-rajdhani text-white/70 mb-6">
                I'm just doing what I love. This is my hobby, my creative outlet, and I believe in what I'm making.
              </p>
              <span className="text-2xl md:text-3xl font-orbitron font-bold bg-gradient-to-r from-cyber-pink to-cyber-cyan bg-clip-text text-transparent">
                Unapologetically.
              </span>
            </motion.div>

            {/* Signature */}
            <motion.div
              className="mt-8 text-right"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 1 }}
            >
              <p className="font-mono text-cyber-cyan text-sm tracking-widest">
                — WHITE RABBIT BANGERS
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* Decorative element */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1.2 }}
        >
          <p className="font-mono text-white/40 text-sm tracking-widest">
            {'>'} NO APOLOGIES. JUST BANGERS._
          </p>
        </motion.div>
      </div>
    </section>
  );
}

